import { useState } from 'react';
import FooterSection from './FooterSection';

const DeleteAccount = () => {
  const [contact, setContact] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const [verifiedContact, setVerifiedContact] = useState('');
  const [personnelId, setPersonnelId] = useState(null);

  const extractPersonnel = (result) => {
    if (!result) return null;

    const data = result?.data;

    if (Array.isArray(data) && data.length > 0) return data[0];
    if (Array.isArray(result) && result.length > 0) return result[0];
    if (data && typeof data === 'object') return data;
    if (result?.personnel && typeof result.personnel === 'object') {
      return result.personnel;
    }
    if (result && typeof result === 'object') return result;

    return null;
  };

  const searchPersonnel = async (contactValue) => {
    if (!contactValue.trim()) {
      setSearchMessage('');
      return null;
    }

    try {
      setIsSearching(true);
      setSearchMessage('');

      const response = await fetch(
        `https://care-api.equily.ng/api/staff/search?query=${encodeURIComponent(contactValue)}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error('Unable to verify this user right now.');
      }

      const result = await response.json();
      const matchedPersonnel = extractPersonnel(result);
      const matchedId =
        matchedPersonnel?.id ||
        matchedPersonnel?._id ||
        matchedPersonnel?.personnel_id ||
        null;

      const hasMatch = Boolean(matchedPersonnel && matchedId);

      if (!hasMatch) {
        setVerifiedContact('');
        setPersonnelId(null);
        setSearchMessage('No user found with that email or phone number.');
        return null;
      }

      setVerifiedContact(contactValue);
      setPersonnelId(matchedId);
      setSearchMessage('User found. You can submit the delete request.');
      return matchedId;
    } catch (lookupError) {
      setVerifiedContact('');
      setPersonnelId(null);
      setSearchMessage(
        lookupError.message || 'Unable to verify this user right now.'
      );
      return null;
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!contact.trim() || !reason.trim()) {
      setError('Please provide your email or phone number and a reason.');
      setSubmitted(false);
      return;
    }

    const trimmedContact = contact.trim();
    const isAlreadyVerified = verifiedContact === trimmedContact;
    let resolvedPersonnelId =
      isAlreadyVerified && personnelId ? personnelId : null;

    if (!resolvedPersonnelId) {
      resolvedPersonnelId = await searchPersonnel(trimmedContact);
    }

    if (!resolvedPersonnelId) {
      setError('Please enter a valid email or phone number linked to a user.');
      setSubmitted(false);
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      setSubmitted(false);

      const response = await fetch(`https://care-api.equily.ng/api/staff/${resolvedPersonnelId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contact: trimmedContact,
          reason,
          requestType: 'delete-account',
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to submit your delete request right now.');
      }

      setSubmitted(true);
      setContact('');
      setReason('');
      setVerifiedContact('');
      setPersonnelId(null);
      setSearchMessage('');
    } catch (submitError) {
      setError(
        submitError.message ||
          'Something went wrong while submitting your delete request.'
      );
      setSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className='bg-orange-50 py-16 px-6'>
        <div className='max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-10'>
          <h1 className='text-3xl font-Anton text-orange-900 mb-4'>
            Delete Account
          </h1>
          <p className='text-gray-700 mb-8'>
            Fill this form to request account deletion.
          </p>

          <form onSubmit={handleSubmit} className='space-y-6'>
            <div>
              <label
                htmlFor='delete-contact'
                className='block text-sm font-semibold text-gray-800 mb-2'
              >
                Email or Phone Number
              </label>
              <input
                id='delete-contact'
                type='text'
                value={contact}
                onChange={(event) => {
                  setContact(event.target.value);
                  setVerifiedContact('');
                  setPersonnelId(null);
                  setSearchMessage('');
                  setSubmitted(false);
                }}
                onBlur={() => searchPersonnel(contact.trim())}
                placeholder='Enter your email or phone number'
                className='w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500'
              />
              {isSearching && (
                <p className='text-gray-600 text-sm mt-2'>Checking user...</p>
              )}
              {!isSearching && searchMessage && (
                <p
                  className={`text-sm mt-2 font-medium ${
                    verifiedContact
                      ? 'text-green-700'
                      : 'text-red-600'
                  }`}
                >
                  {searchMessage}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor='delete-reason'
                className='block text-sm font-semibold text-gray-800 mb-2'
              >
                Reason for Deleting
              </label>
              <textarea
                id='delete-reason'
                rows='5'
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder='Tell us why you want to delete your account'
                className='w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500'
              />
            </div>

            {error && <p className='text-red-600 text-sm font-medium'>{error}</p>}

            {submitted && (
              <p className='text-green-700 text-sm font-medium'>
                Your delete account request has been submitted.
              </p>
            )}

            <button
              type='submit'
              disabled={isSubmitting}
              className='bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-md'
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </section>
      <FooterSection />
    </>
  );
};

export default DeleteAccount;